import { Link } from "react-router-dom";
import { solutions } from "@/mocks/homeData";
import { useScrollReveal } from "@/hooks/useScrollReveal";

export default function SolutionsSection() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal();
  const { ref: cardsRef, isVisible: cardsVisible } = useScrollReveal({ threshold: 0.1 });

  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div
          ref={headerRef}
          className={`flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div>
            <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">
              What We Offer
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] leading-tight">
              Solutions Designed for <br className="hidden md:block" />
              <strong>Real Business Challenges</strong>
            </h2>
          </div>
          <p className="text-gray-500 text-sm leading-relaxed max-w-sm">
            We don't just sell products — we deliver integrated solutions tailored to your industry's specific needs, combining best-in-class brands into cohesive, results-driven systems.
          </p>
        </div>

        {/* Solution Cards */}
        <div ref={cardsRef} className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {solutions.map((sol, i) => (
            <Link
              key={sol.id}
              to={sol.href}
              className={`group relative overflow-hidden rounded-lg cursor-pointer transition-all duration-700 ease-out ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
              style={{ transitionDelay: cardsVisible ? `${i * 120}ms` : "0ms" }}
            >
              {/* Image — zooms on hover */}
              <div className="w-full h-80 overflow-hidden">
                <img
                  src={sol.image}
                  alt={sol.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-110"
                />
              </div>

              {/* Base gradient — always visible, fades out on hover */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d]/85 via-[#0d0d0d]/20 to-transparent transition-opacity duration-500 group-hover:opacity-0" />

              {/* Hover overlay — rich dark fill fades in */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d]/95 via-[#0d0d0d]/70 to-[#0d0d0d]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              {/* Accent line — grows from bottom on hover */}
              <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#e63b2e] scale-y-0 group-hover:scale-y-100 transition-transform duration-500 ease-out origin-bottom" />

              {/* Default state — title + subtitle, fades out on hover */}
              <div className="absolute bottom-0 left-0 right-0 p-6 transition-all duration-400 group-hover:translate-y-3 group-hover:opacity-0">
                <span className="text-xs font-semibold tracking-widest uppercase text-white/50 mb-2 block">
                  {sol.subtitle}
                </span>
                <h3 className="text-xl font-bold text-white leading-snug">{sol.title}</h3>
              </div>

              {/* Hover caption — slides up and fades in */}
              <div className="absolute inset-0 flex flex-col justify-end p-6 translate-y-6 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 ease-out pointer-events-none">
                {/* Red category line */}
                <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-widest uppercase text-[#e63b2e] mb-3">
                  <span className="w-4 h-px bg-[#e63b2e] inline-block" />
                  {sol.subtitle}
                </span>

                {/* Title */}
                <h3 className="text-2xl font-bold text-white leading-tight mb-3">{sol.title}</h3>

                {/* Thin divider */}
                <div className="w-10 h-[2px] bg-white/25 mb-4" />

                {/* Description */}
                <p className="text-white/75 text-sm leading-relaxed mb-5">
                  {sol.description}
                </p>

                {/* Brand pills */}
                <div className="flex items-center gap-2 flex-wrap mb-5">
                  {sol.brands.map((b) => (
                    <span
                      key={b}
                      className="text-[11px] font-medium bg-white/10 border border-white/20 text-white/90 px-2.5 py-1 rounded-full"
                    >
                      {b}
                    </span>
                  ))}
                </div>

                {/* CTA row */}
                <div className="flex items-center gap-2 text-white text-sm font-semibold">
                  <span>Explore Solution</span>
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#e63b2e] transition-transform duration-300 group-hover:translate-x-1">
                    <i className="ri-arrow-right-line text-xs text-white" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
